/**
 * CanadianDoublesModal - Interactive Hand Organizer for Canadian Doubles
 * Allows dealer and pone to organize their 10 dealt cards:
 * - Keep 4 cards for their own hand
 * - Pass 4 cards to their respective partner
 * - Discard 2 cards into the crib
 */

import { CribbageScorer } from '../scoring/CribbageScorer.js';
import { CribbageAI } from '../ai/CribbageAI.js';
import { ModalManager } from '../ui/ModalManager.js';

export class CanadianDoublesModal {
  /**
   * Opens the interactive Canadian Doubles organizer modal.
   * @param {string} organizerSeat - The seat organizing (pone or dealer)
   * @param {Object} [context={}]
   */
  static open(organizerSeat, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const setSeatHand = context.setSeatHand || (typeof window !== 'undefined' ? window.setSeatHand : () => {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' ? window.setSeatKept : () => {});
    const showModal = context.showModal || (typeof window !== 'undefined' ? (window.modalManager ? window.modalManager.showModal.bind(window.modalManager) : (window.showModal || ModalManager.showModal)) : ModalManager.showModal);
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? (window.modalManager ? window.modalManager.hideModal.bind(window.modalManager) : (window.hideModal || ModalManager.hideModal)) : ModalManager.hideModal);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const onComplete = context.onComplete || (() => {});

    // Identify partner seat in 4-player cribbage: P1 <-> P3, P2 <-> P4
    const partnerSeat = organizerSeat === 'p1' ? 'p3' : (organizerSeat === 'p2' ? 'p4' : (organizerSeat === 'p3' ? 'p1' : 'p2'));
    const isDealer = organizerSeat === state.dealer || partnerSeat === state.dealer;

    // Retrieve dealt 10 cards
    const rawCards = (getSeatHand(organizerSeat) || []).slice();
    const tenCards = rawCards.length >= 10 ? rawCards.slice(0, 10) : rawCards;

    // Run AI to find optimal default partition
    const optimal = CribbageAI.organizeCanadianDoubles(tenCards, isDealer, { settings, starter: state.starter });

    let keepHand = optimal && optimal.keepHand ? [...optimal.keepHand] : tenCards.slice(0, 4);
    let partnerHand = optimal && optimal.partnerHand ? [...optimal.partnerHand] : tenCards.slice(4, 8);
    let cribDiscards = optimal && optimal.cribDiscards ? [...optimal.cribDiscards] : tenCards.slice(8, 10);

    let selected = null; // { zone: 'keep'|'partner'|'crib', idx: number }
    let toastMsg = '💡 Cards auto-arranged for highest score. Click cards in different sections to swap.';

    const rankNames = { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' };
    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };

    function renderSingleCard(c, zone, idx, isSelected) {
      const suit = c.suit || c.s || 'S';
      const isRed = suit === 'H' || suit === 'D';
      const color = isRed ? '#dc2626 !important' : '#111827 !important';
      const r = c.rankName || c.r || rankNames[c.rank] || c.rank || '★';
      const sym = suitSyms[suit] || suit || '';

      const cardBorder = isSelected
        ? 'border: 2px solid #f59e0b;'
        : 'border: 2px solid #cbd5e1;';
      const cardShadow = isSelected
        ? 'box-shadow: 0 0 16px rgba(245, 158, 11, 0.95), 0 4px 14px rgba(0,0,0,0.5); transform: translateY(-6px);'
        : 'box-shadow: 0 4px 10px rgba(0,0,0,0.35);';

      const zoneLabel = zone === 'keep' ? 'You' : (zone === 'partner' ? 'Partner' : 'Crib');

      return `
        <div class="playing-card canadian-card ${isRed ? 'red card-red' : 'black card-black'}${isSelected ? ' selected' : ''}"
             id="canCard_${zone}_${idx}"
             data-action="canadian-card"
             data-zone="${zone}"
             data-index="${idx}"
             style="position: relative; width: 62px; height: 90px; box-sizing: border-box; background: #ffffff !important; border-radius: 8px; display: flex; flex-direction: column; justify-content: space-between; padding: 5px 6px; text-align: left; cursor: pointer; transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease; ${cardBorder} ${cardShadow}">
          <div class="canadian-card-corner" style="font-size: 1rem; font-weight: 900; color: ${color}; -webkit-text-fill-color: ${color}; line-height: 1;">${r}<span style="font-size: 0.85rem; margin-left: 2px; color: ${color}; -webkit-text-fill-color: ${color};">${sym}</span></div>
          <div class="canadian-card-center" style="font-size: 1.7rem; color: ${color}; -webkit-text-fill-color: ${color}; line-height: 1; text-align: center;">${sym}</div>
          <div class="canadian-card-label" style="font-size: 0.62rem; font-weight: 700; color: #64748b !important; -webkit-text-fill-color: #64748b !important; text-align: center; text-transform: uppercase;">${zoneLabel}</div>
        </div>
      `;
    }

    function renderModalContent() {
      const scoreOpts = { verbose: true, ...settings };
      const s1 = keepHand.length === 4 ? (CribbageScorer.scoreHand(keepHand, state.starter, false, scoreOpts) || { total: 0, notes: [] }) : { total: 0, notes: [] };
      const s2 = partnerHand.length === 4 ? (CribbageScorer.scoreHand(partnerHand, state.starter, false, scoreOpts) || { total: 0, notes: [] }) : { total: 0, notes: [] };
      const sCrib = cribDiscards.length === 2 ? (CribbageScorer.scoreHand(cribDiscards, state.starter, true, scoreOpts) || { total: 0, notes: [] }) : { total: 0, notes: [] };

      const teamTotal = s1.total + s2.total;

      const formatNotesList = (notes) => {
        if (!notes || !notes.length) return '<span style="color:#64748b;">No combinations</span>';
        return notes.map(n => `<span style="display:inline-block; margin-right:8px; color:#cbd5e1;">${n}</span>`).join('· ');
      };

      const keepCardsHtml = keepHand.map((c, i) => renderSingleCard(c, 'keep', i, selected && selected.zone === 'keep' && selected.idx === i)).join('');
      const partnerCardsHtml = partnerHand.map((c, i) => renderSingleCard(c, 'partner', i, selected && selected.zone === 'partner' && selected.idx === i)).join('');
      const cribCardsHtml = cribDiscards.map((c, i) => renderSingleCard(c, 'crib', i, selected && selected.zone === 'crib' && selected.idx === i)).join('');

      const myName = seatName(organizerSeat);
      const partnerName = seatName(partnerSeat);
      const cribTitle = isDealer ? `YOUR TEAM'S CRIB (${seatName(state.dealer)})` : `OPPONENT'S CRIB (${seatName(state.dealer)})`;

      return `
        <div class="canadian-modal-box">
          <div class="canadian-badge">🍁 CANADIAN DOUBLES — HAND DISTRIBUTION</div>
          <h2 class="canadian-modal-title">Organize Your 10 Cards</h2>
          <div class="canadian-modal-subtitle">
            ${myName}, keep 4 cards for your hand, pass 4 to your partner (${partnerName}), and discard 2 to the crib.
          </div>

          <div class="canadian-scoreboard">
            <div style="text-align:right;">
              <div style="font-size:0.75rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Your Hand (${myName})</div>
              <div style="font-size:1.35rem; font-weight:800; color:#60a5fa;">+${s1.total} pts</div>
            </div>
            <div style="font-size:1.6rem; font-weight:900; color:#64748b;">+</div>
            <div style="text-align:left;">
              <div style="font-size:0.75rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Partner (${partnerName})</div>
              <div style="font-size:1.35rem; font-weight:800; color:#c084fc;">+${s2.total} pts</div>
            </div>
            <div style="font-size:1.6rem; font-weight:900; color:#64748b;">=</div>
            <div style="border-left:2px solid rgba(255,255,255,0.15); padding-left:14px; text-align:left;">
              <div style="font-size:0.75rem; text-transform:uppercase; color:#fde047; font-weight:800; letter-spacing:0.05em;">TEAM HAND TOTAL</div>
              <div class="canadian-score-total">${teamTotal} PTS</div>
            </div>
          </div>

          ${toastMsg ? `<div style="margin: 4px auto 10px; color:#38bdf8; font-weight:700; font-size:0.84rem;">${toastMsg}</div>` : ''}

          <div class="canadian-hands-grid">
            <div class="canadian-hand-panel hand-you">
              <div class="canadian-hand-header">
                <span class="canadian-hand-name">YOUR HAND (${myName}) · 4 CARDS</span>
                <span class="canadian-hand-pts">${s1.total} pts</span>
              </div>
              <div class="canadian-cards-row">
                ${keepCardsHtml}
              </div>
              <div class="canadian-notes">
                ${formatNotesList(s1.notes)}
              </div>
            </div>

            <div class="canadian-hand-panel hand-partner">
              <div class="canadian-hand-header">
                <span class="canadian-hand-name">PASS TO PARTNER (${partnerName}) · 4 CARDS</span>
                <span class="canadian-hand-pts">${s2.total} pts</span>
              </div>
              <div class="canadian-cards-row">
                ${partnerCardsHtml}
              </div>
              <div class="canadian-notes">
                ${formatNotesList(s2.notes)}
              </div>
            </div>
          </div>

          <div class="canadian-hand-panel hand-crib" style="max-width: 480px; margin: 0 auto 16px;">
            <div class="canadian-hand-header">
              <span class="canadian-hand-name">${cribTitle} · 2 CARDS</span>
              <span class="canadian-hand-pts" style="${isDealer ? 'color:#4ade80; border-color:rgba(74,222,128,0.4);' : 'color:#f87171; border-color:rgba(248,113,113,0.4);'}">${sCrib.total} pts</span>
            </div>
            <div class="canadian-cards-row">
              ${cribCardsHtml}
            </div>
            <div style="font-size: 0.72rem; color: #94a3b8; text-align: center;">
              ${isDealer ? '⭐ Your team scores the crib!' : "⚠️ Opponents score this crib. Discard safe cards!"}
            </div>
          </div>

          <div class="canadian-actions">
            <button type="button" class="btn-canadian-opt" data-action="canadian-opt">
              ⚡ Auto-Optimize (Best Scores)
            </button>
            <button type="button" class="btn-canadian-confirm" data-action="canadian-confirm">
              🍁 Confirm Hand Distribution
            </button>
          </div>
        </div>
      `;
    }

    function refreshUI() {
      const modal = document.getElementById('modal');
      if (modal) {
        modal.innerHTML = renderModalContent();
      }
    }

    window.onCanadianCardClick = (zone, idx) => {
      if (!selected) {
        selected = { zone, idx };
        toastMsg = `Selected card from ${zone === 'keep' ? 'Your Hand' : (zone === 'partner' ? "Partner's Hand" : 'Crib')}. Click another card to swap.`;
        refreshUI();
        return;
      }

      if (selected.zone === zone && selected.idx === idx) {
        selected = null;
        toastMsg = 'Selection cleared.';
        refreshUI();
        return;
      }

      // Swap between zones or within zone
      const getArray = (z) => z === 'keep' ? keepHand : (z === 'partner' ? partnerHand : cribDiscards);
      const arr1 = getArray(selected.zone);
      const arr2 = getArray(zone);

      const temp = arr1[selected.idx];
      arr1[selected.idx] = arr2[idx];
      arr2[idx] = temp;

      selected = null;
      toastMsg = 'Cards swapped!';
      refreshUI();
    };

    window.onCanadianAutoOptimize = () => {
      const all10 = [...keepHand, ...partnerHand, ...cribDiscards];
      const opt = CribbageAI.organizeCanadianDoubles(all10, isDealer, { settings, starter: state.starter });
      if (opt) {
        keepHand = [...opt.keepHand];
        partnerHand = [...opt.partnerHand];
        cribDiscards = [...opt.cribDiscards];
        selected = null;
        toastMsg = '⚡ Optimal partition restored!';
        refreshUI();
      }
    };

    window.onCanadianConfirm = () => {
      if (keepHand.length !== 4 || partnerHand.length !== 4 || cribDiscards.length !== 2) {
        toastMsg = '⚠️ Invalid card counts! Must have 4 in Hand, 4 to Partner, and 2 to Crib.';
        refreshUI();
        return;
      }

      // Cleanup window handlers
      const modalEl = document.getElementById('modal');
      if (modalEl) modalEl.removeEventListener('click', handleCanadianClick);

      delete window.onCanadianCardClick;
      delete window.onCanadianAutoOptimize;
      delete window.onCanadianConfirm;

      // Apply distribution
      setSeatHand(organizerSeat, keepHand);
      setSeatKept(organizerSeat, keepHand.slice());
      setSeatHand(partnerSeat, partnerHand);
      setSeatKept(partnerSeat, partnerHand.slice());

      state.crib = state.crib || [];
      state.crib.push(...cribDiscards);
      state.seatDiscards = state.seatDiscards || {};
      state.seatDiscards[organizerSeat] = cribDiscards;

      const myName = seatName(organizerSeat);
      const partnerName = seatName(partnerSeat);
      log(`Canadian Doubles: ${myName} kept 4 cards, passed 4 cards to ${partnerName}, and discarded 2 cards to the crib.`);
      fancyCall(`${myName} distributed 10 cards!`, 'combo', 1500);

      if (typeof hideModal === 'function') {
        hideModal();
      } else {
        const overlay = document.getElementById('overlay');
        if (overlay) overlay.style.display = 'none';
      }

      render();
      onComplete(organizerSeat);
    };

    const handleCanadianClick = (e) => {
      const card = e.target.closest('[data-action="canadian-card"], .canadian-card');
      if (card && card.dataset.zone !== undefined && card.dataset.index !== undefined) {
        const zone = card.dataset.zone;
        const idx = parseInt(card.dataset.index, 10);
        if (typeof window.onCanadianCardClick === 'function') {
          window.onCanadianCardClick(zone, idx);
        }
        return;
      }
      const optBtn = e.target.closest('[data-action="canadian-opt"], .btn-canadian-opt');
      if (optBtn) {
        if (typeof window.onCanadianAutoOptimize === 'function') {
          window.onCanadianAutoOptimize();
        }
        return;
      }
      const confirmBtn = e.target.closest('[data-action="canadian-confirm"], .btn-canadian-confirm');
      if (confirmBtn) {
        if (typeof window.onCanadianConfirm === 'function') {
          window.onCanadianConfirm();
        }
        return;
      }
    };

    if (typeof showModal === 'function') {
      showModal(renderModalContent(), false);
    } else {
      const modal = document.getElementById('modal');
      const overlay = document.getElementById('overlay');
      if (modal && overlay) {
        modal.innerHTML = renderModalContent();
        overlay.style.display = 'flex';
      }
    }

    const modalContainer = document.getElementById('modal');
    if (modalContainer) {
      modalContainer.addEventListener('click', handleCanadianClick);
    }
  }
}
